import React from 'react';
import { usePageContent } from '../../services/contentService';
import LoadingOverlay from '../../components/LoadingOverlay';
import './WhyItMatters.css';
import SEO from '../../components/SEO';

const WhyItMatters = () => {
  const { content, loading, error } = usePageContent('why-it-matters');
  console.log('Why It Matters page content:', content?.sections?.length || 0, loading, error);

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: window.innerWidth <= 768 ? '0 1rem' : '0 2rem'
  };

  if (loading || !content) {
    return <LoadingOverlay />;
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error loading content: {error}</p>
      </div>
    );
  }

  // Get sections from content
  const heroSection = content?.sections?.find(s => s.type === 'page-hero')?.content;
  const statisticsSection = content?.sections?.find(s => s.type === 'statistics')?.content;

  return (
    <div className="why-it-matters-page">
      <SEO
        title="Why It Matters"
        description="Discover why empowering Africa's youth matters. We address unemployment and lack of opportunities by providing skills and funding."
      />
      {/* Hero Section */}
      <div className="why-hero-section">
        <div style={containerStyle}>
          <div className="why-hero-content">
            <h1 className="why-hero-title">
              {heroSection?.mainTitle || "Why It Matters"}
            </h1>
            <p className="why-hero-description">
              {heroSection?.description || "Every day, millions of young Africans face unemployment, lack of opportunities, and barriers to innovation. We are changing that."}
            </p>
            <button className="donate-cta-btn">
              Donate
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Our Why Section */}
      {statisticsSection && (
        <div className="our-why-section">
          <div style={containerStyle}>
            <div className="section-header-center">
              <span className="section-label-orange">{statisticsSection.sectionLabel || "OUR WHY"}</span>
              {statisticsSection.mainTitle && (
                <h2 className="section-title-center">{statisticsSection.mainTitle}</h2>
              )}
            </div>

            <div className="why-stats-grid">
              {statisticsSection.statistics?.map((stat, index) => (
                <div key={index} className="stat-item">
                  <div className="stat-number">{stat.number}</div>
                  <p className="stat-description">{stat.description}</p>
                </div>
              ))}
            </div>

            <div className="why-explanation">
              <p>
                Africa's youth face a triple challenge: limited access to quality skills training,
                low job opportunities, and scarce funding for their ideas. This gap keeps millions
                from reaching their full potential and slows the continent's growth.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Why It Matters Details */}
      <div className="why-matters-details">
        <div style={containerStyle}>
          <div className="why-content-layout">
            <div className="why-text-content">
              <h2 className="why-section-title">Why It Matters</h2>
              <p className="why-intro">
                AT YOCILAB we believe that when youth are left without opportunities, entire
                communities lose out. Unemployment leads to poverty, social unrest, and wasted
                potential. But when young people are empowered with skills, mentorship, and
                funding, they create jobs, solve local challenges, and drive sustainable development.
              </p>

              <div className="impact-stats">
                <div className="impact-stat">
                  <div className="impact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2L13.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" />
                    </svg>
                  </div>
                  <p>Every $1 invested in youth skills can return up to $4 in economic growth.</p>
                </div>
                <div className="impact-stat">
                  <div className="impact-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 11H7v9h2v-9zm4-4h-2v13h2V7zm4-4h-2v17h2V3z" />
                    </svg>
                  </div>
                  <p>Youth-led innovations are solving local problems from climate change to food security, but lack of funding stalls their impact.</p>
                </div>
              </div>
            </div>
            <div className="why-image-content">
              <img
                src={require('../../assets/YOCILAB/IMG_3206 (1).jpg')}
                alt="Young African innovators at work"
                className="why-feature-image"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Our Approach Section */}
      <div className="our-approach-section">
        <div style={containerStyle}>
          <div className="section-header-center">
            <span className="section-label-orange">OUR APPROACH</span>
          </div>

          <div className="approach-grid">
            <div className="approach-card">
              <div className="approach-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9M15 9.5C15 11.8 16.76 14.15 18.5 16C19.27 16.93 20 18.22 20 19.5C20 20.61 19.11 21.5 18 21.5C16.89 21.5 16 20.61 16 19.5L14 19.5C14 21.71 15.79 23.5 18 23.5C20.21 23.5 22 21.71 22 19.5C22 17.69 20.92 15.82 19.75 14.5C18.69 13.29 18 12.25 18 11.5C18 10.12 16.88 9 15.5 9C14.12 9 13 10.12 13 11.5V12H15V11.5C15 11.22 15.22 11 15.5 11C15.78 11 16 11.22 16 11.5C16 11.73 16.38 12.42 17.13 13.37C18.69 15.5 20.5 18.08 20.5 19.5C20.5 20.33 19.83 21 19 21C18.17 21 17.5 20.33 17.5 19.5C17.5 18.67 18.17 18 19 18C19.83 18 20.5 18.67 20.5 19.5" />
                </svg>
              </div>
              <h3 className="approach-title">Skills & Training</h3>
              <p className="approach-description">
                We equip youth with in-demand skills, from entrepreneurship and digital literacy
                to climate-smart innovation to prepare them for jobs and leadership roles.
              </p>
            </div>

            <div className="approach-card">
              <div className="approach-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 11H7v9h2v-9zm4-4h-2v13h2V7zm4-4h-2v17h2V3z" />
                </svg>
              </div>
              <h3 className="approach-title">Innovation Support</h3>
              <p className="approach-description">
                Through our hubs and programs, young changemakers access mentorship,
                collaboration spaces, and tools to bring their ideas to life.
              </p>
            </div>

            <div className="approach-card">
              <div className="approach-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" />
                </svg>
              </div>
              <h3 className="approach-title">Funding & Opportunities</h3>
              <p className="approach-description">
                We connect youth to investors, grants, and seed funding so they can launch
                and scale solutions that impact their communities.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Our Impact Section */}
      <div className="our-impact-section">
        <div style={containerStyle}>
          <div className="section-header-center">
            <span className="section-label-orange">OUR IMPACT</span>
          </div>

          <div className="impact-grid">
            <div className="impact-metric">
              <div className="metric-number">3,500+</div>
              <p className="metric-label">youth trained in skills for the future</p>
            </div>
            <div className="impact-metric">
              <div className="metric-number">80+</div>
              <p className="metric-label">youth-led projects launched</p>
            </div>
            <div className="impact-metric">
              <div className="metric-number">15</div>
              <p className="metric-label">partnerships with schools & community groups</p>
            </div>
            <div className="impact-metric">
              <div className="metric-number">$50,000+</div>
              <p className="metric-label">in funding secured for youth initiatives</p>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="final-cta-section">
        <div style={containerStyle}>
          <div className="cta-content">
            <h2 className="cta-title">
              Every young person deserves the chance to create change.
              Together, we can make it happen.
            </h2>
            <div className="cta-buttons">
              <button className="join-us-btn">Join Us</button>
              <button className="donate-now-btn">Donate Now</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyItMatters;